/**
 * Clarity event tracking utilities for UX metrics
 *
 * Extends Clarity setup (useClarity.ts) with event tracking capabilities.
 * Consent and user identification are handled by useClarityConsent hook.
 *
 * Usage:
 * - trackEvent: Track user actions and interactions
 * - trackError: Track errors encountered by users
 * - trackTiming: Track performance-related metrics
 */

import Clarity from "@microsoft/clarity";

const clarityProjectId = import.meta.env.VITE_MS_CLARITY_PROJECT_ID;

/**
 * Check if Clarity is configured and available
 */
const isClarityAvailable = (): boolean => {
  return Boolean(clarityProjectId);
};

/**
 * Track a custom event in Clarity
 * @param eventName - Name of the event (use snake_case convention)
 * @example trackEvent('claim_submission_started')
 */
export const trackEvent = (eventName: string): void => {
  if (!isClarityAvailable()) {
    if (import.meta.env.DEV) {
      console.log("[Clarity] Event:", eventName);
    }
    return;
  }

  try {
    Clarity.event(eventName);
  } catch (error) {
    console.error("[Clarity] Failed to track event:", eventName, error);
  }
};

/**
 * Track an error encountered by the user
 * @param context - Where the error occurred (e.g., 'claim_submission', 'document_upload')
 * @param errorType - Optional type of error for categorization
 * @example trackError('claim_submission', 'validation_error')
 */
export const trackError = (context: string, errorType?: string): void => {
  const eventName = errorType ? `error_${context}_${errorType}` : `error_${context}`;
  trackEvent(eventName);
};

/**
 * Track timing for performance-related metrics
 * Returns a function to call when the operation completes
 * @param operation - Name of the operation being timed
 * @returns Function to call when operation completes
 * @example
 * const endTiming = trackTiming('report_generation');
 * // ... do work ...
 * endTiming();
 */
export const trackTiming = (operation: string): (() => void) => {
  trackEvent(`${operation}_started`);

  return () => {
    trackEvent(`${operation}_completed`);
  };
};

/**
 * Predefined event names for shared events used across multiple components.
 * Add events here only when they're used in 2+ places.
 * Component-specific events should use string literals directly.
 */
export const ClarityEvents = {
  // Add shared events here as needed
  SEARCH_EXECUTED: "search_executed",
  FILTER_APPLIED: "filter_applied",
} as const;

// Re-export Clarity.setTag for convenience (so components don't need two imports)
export const setTag = (key: string, value: string): void => {
  if (isClarityAvailable()) {
    Clarity.setTag(key, value);
  }
};
